import { useState } from 'react';
import { ChevronsUpDown, Plus, Search } from 'lucide-react';
import { Input, MenuItem, MenuSeparator, MenuSub, MenuSubContent, MenuSubTrigger } from '@strivelabs/ui-core/v2';
import { cn } from '@strivelabs/ui-core/lib/utils';
import { AddAccountModal } from '@/components/auth/AddAccountModal';
import { useOrgSwitcher } from '../hooks/useOrgSwitcher';

/**
 * Organisation avatar: the logo when there is one, otherwise the
 * organisation's own initial on a neutral tint. A generic building glyph made
 * every organisation in the list look identical, which is the one thing this
 * mark exists not to do.
 */
export function OrgMark({
  name,
  imageUrl,
  className,
}: {
  name?: string;
  imageUrl?: string;
  className?: string;
}) {
  if (imageUrl) {
    return (
      <img
        src={imageUrl}
        alt=""
        className={cn('size-5 shrink-0 rounded-v2-md object-cover outline outline-1 -outline-offset-1 outline-black/10', className)}
      />
    );
  }
  return (
    <span
      aria-hidden
      className={cn(
        'grid size-5 shrink-0 place-items-center rounded-v2-md bg-v2-popover-foreground/[0.05] text-[10px] font-medium',
        className,
      )}
    >
      {name?.trim().charAt(0).toUpperCase()}
    </span>
  );
}

const ROW_CLASS = 'gap-[9px] rounded-v2-lg py-[5px] pl-1 pr-[7px] text-[13px] font-medium leading-[18px]';

/**
 * The organisation row inside the account panel (Home Shell design). It names
 * the current organisation and, when there is anywhere else to go, opens a
 * side menu with a search field, one item per membership and "Add
 * organisation" — on hover, like any submenu, and on click, because the design
 * treats the row as a button. With a single organisation and no create
 * permission it is a static row: a menu with one item and no other action is
 * just noise.
 */
export function OrgSubmenu() {
  const [open, setOpen] = useState(false);
  const [isAddAccountOpen, setIsAddAccountOpen] = useState(false);
  const [query, setQuery] = useState('');
  const { isReady, organization, memberships, hasMultipleOrgs, canCreateOrg, switchOrg } =
    useOrgSwitcher();

  const visibleMemberships = query.trim()
    ? memberships.filter(entry =>
        entry.organization.name.toLowerCase().includes(query.trim().toLowerCase()),
      )
    : memberships;

  const name = organization?.name ?? 'No organisation';

  const rowContent = (
    <span className="flex min-w-0 flex-1 items-center gap-[9px]">
      <OrgMark name={name} imageUrl={organization?.imageUrl} />
      <span className="min-w-0 flex-1 truncate">{name}</span>
    </span>
  );

  if (!isReady) {
    return (
      <div className="border-b border-[#EDEEF0] px-1.5 py-2">
        <div aria-hidden className="h-[30px] animate-pulse rounded-v2-lg bg-v2-sidebar-accent motion-reduce:animate-none" />
      </div>
    );
  }

  if (!hasMultipleOrgs && !canCreateOrg) {
    return (
      <div className="border-b border-[#EDEEF0] px-1.5 py-2">
        <div className={cn('flex items-center text-v2-popover-foreground', ROW_CLASS)}>{rowContent}</div>
      </div>
    );
  }

  return (
    <div className="border-b border-[#EDEEF0] px-1.5 py-2">
      <MenuSub open={open} onOpenChange={setOpen}>
        {/* `MenuSub` already toggles on click as well as hover, so the row
            needs no handler of its own — a second toggle would cancel the first. */}
        <MenuSubTrigger
          aria-label={`Current organisation: ${name}. Switch organisation`}
          className={ROW_CLASS}
          // An up/down caret, not the submenu default's right caret: this row
          // swaps which organisation is current rather than opening a level.
          chevron={<ChevronsUpDown size={13} aria-hidden className="shrink-0 text-v2-popover-foreground/50" />}
        >
          {rowContent}
        </MenuSubTrigger>
        {/* Named by its trigger row (aria-labelledby), which ends in
            "Switch organisation" — the name the E2E page object looks for. */}
        <MenuSubContent className="w-[256px]">
          <div
            className="-mx-1 -mt-1 mb-1 border-b border-v2-border p-1.5"
            // The menu runs typeahead on keystrokes; without this the search field
            // would lose every character to it.
            onKeyDown={event => event.stopPropagation()}
          >
            <Input
              type="search"
              value={query}
              onChange={setQuery}
              aria-label="Search organisations"
              placeholder="Search organisations"
              leftIcon={<Search size={13} />}
              classNames={{ field: 'h-7 rounded-v2-md', input: 'text-v2-sm' }}
            />
          </div>

          <div className="max-h-[280px] overflow-y-auto">
            {visibleMemberships.length === 0 ? (
              <p className="px-2 py-3 text-center text-v2-xs text-v2-muted-foreground">
                No organisations found
              </p>
            ) : null}
            {visibleMemberships.map(({ organization: org }) => {
              const isActive = org.id === organization?.id;
              return (
                <MenuItem
                  key={org.id}
                  label={org.name}
                  // The row's own tint carries the selection, as the design
                  // has it -- a tick as well read as a checklist. `aria-current`
                  // keeps that legible to a screen reader, which cannot see it.
                  aria-current={isActive ? 'true' : undefined}
                  className={cn(
                    'gap-2 py-1.5 tracking-[-0.24px]',
                    isActive
                      ? 'bg-v2-sidebar-active font-medium text-v2-popover-foreground hover:bg-v2-sidebar-active'
                      : 'text-v2-popover-foreground/[0.78]',
                  )}
                  onClick={() => {
                    if (!isActive) switchOrg(org.id);
                  }}
                >
                  <OrgMark
                    name={org.name}
                    imageUrl={org.imageUrl}
                    className={isActive ? 'text-v2-popover-foreground' : 'text-v2-popover-foreground/[0.56]'}
                  />
                  <span className="min-w-0 flex-1 truncate">{org.name}</span>
                </MenuItem>
              );
            })}
          </div>

          {canCreateOrg ? (
            <>
              <MenuSeparator />
              <MenuItem label="Add organisation" onClick={() => setIsAddAccountOpen(true)}>
                <span aria-hidden className="grid size-5 shrink-0 place-items-center">
                  <Plus size={14} />
                </span>
                Add organisation
              </MenuItem>
            </>
          ) : null}
        </MenuSubContent>
      </MenuSub>

      <AddAccountModal open={isAddAccountOpen} onOpenChange={setIsAddAccountOpen} />
    </div>
  );
}
